<script setup lang="ts">
import CatalogPagination from '~/components/products/CatalogPagination.vue';
import GalleryPageHeader from '~/components/gallery/GalleryPageHeader.vue';
import { catalogCanonicalPath, catalogPage } from '~/utils/catalogPagination';
import { galleryImageSrcset, type GalleryAlbum, type GalleryPagination } from '~/utils/gallery';

const { t, locale } = useI18n();
const route = useRoute();
const { $api } = useNuxtApp();
const page = computed(() => catalogPage(route.query.page));
const { data, error, status } = await useAsyncData(
  computed(() => `gallery:${locale.value}:${page.value}`),
  () => $api<{ list: GalleryAlbum[]; meta: GalleryPagination }>('gallery', { query: { page: page.value, per_page: 24 } }),
);
if (error.value) throw createError(error.value);
watch(error, value => { if (value) showError(value); });
usePageSeo(computed(() => ({
  name: t('gallery_module.title'), short_description: t('gallery_module.description'),
  photo: data.value?.list[0]?.cover?.photo || null, icon: null, schemaType: 'CollectionPage',
  canonicalPath: catalogCanonicalPath(route.path, route.query.page),
})));
</script>

<template>
  <section class="container-xxl gallery-page" :aria-busy="status === 'pending'">
    <GalleryPageHeader :title="t('gallery_module.title')" />
    <div v-if="data?.list.length" class="gallery-albums">
      <article v-for="(album, index) in data.list" :key="album.id" class="gallery-album">
        <NuxtLinkLocale :to="album.url" class="album-card">
          <div class="album-image">
            <img v-if="album.cover" :src="album.cover.thumb || album.cover.photo" :srcset="galleryImageSrcset(album.cover)" sizes="(min-width: 992px) 30vw, (min-width: 576px) 46vw, 94vw"
              :alt="album.name" width="800" height="600" :loading="index < 3 ? 'eager' : 'lazy'" :fetchpriority="index === 0 ? 'high' : 'auto'" decoding="async" />
            <Icon v-else name="lucide:images" size="48" aria-hidden="true" />
            <span class="album-count">{{ t('gallery_module.photo_count', { count: album.photos_count }) }}</span>
          </div>
          <div class="album-text">
            <h2>{{ album.name }}</h2>
            <p v-if="album.short_description">{{ album.short_description }}</p>
            <span class="album-open">{{ t('gallery_module.view_album') }} <Icon name="lucide:arrow-right" size="18" aria-hidden="true" /></span>
          </div>
        </NuxtLinkLocale>
      </article>
    </div>
    <el-empty v-else :description="t('gallery_module.empty_albums')" />
    <CatalogPagination v-if="data?.meta" :current-page="data.meta.current_page" :last-page="data.meta.last_page" />
  </section>
</template>

<style scoped>
.gallery-albums { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 1.5rem; margin-bottom: 3rem; }
.gallery-album { min-width: 0; }
.album-card { position: relative; display: block; isolation: isolate; height: 100%; overflow: hidden; border-radius: 18px; color: #fff; text-decoration: none; background: var(--el-text-color-primary); }
.album-card::after { content: ''; position: absolute; inset: 0; z-index: 1; background: linear-gradient(180deg, rgba(0,0,0,.12) 15%, rgba(0,0,0,.08) 30%, rgba(0,0,0,.82) 100%); pointer-events: none; }
.album-card:focus-visible { outline: 3px solid var(--el-color-primary); outline-offset: 5px; }
.album-image { position: relative; display: grid; place-items: center; aspect-ratio: 4 / 5; overflow: hidden; background: var(--el-fill-color-dark); color: var(--el-text-color-secondary); }
.album-image img { width: 100%; height: 100%; object-fit: cover; transition: transform .5s ease; }
.album-card:hover img, .album-card:focus-visible img { transform: scale(1.045); }
.album-count { position: absolute; z-index: 2; top: 1.25rem; right: 1.25rem; background: rgba(0,0,0,.45); color: #fff; padding: .4rem .8rem; border: 1px solid rgba(255,255,255,.25); border-radius: 2rem; font-size: var(--fs--1); }
.album-text { position: absolute; z-index: 2; inset: auto 0 0; padding: clamp(1.25rem, 2.5vw, 2rem); }
.album-text h2 { margin: 0 0 .65rem; font-size: clamp(1.4rem, 2vw, 2rem); font-weight: 600; line-height: 1.2; color: #fff; overflow-wrap: anywhere; }
.album-text p { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; margin: 0 0 1.25rem; color: rgba(255,255,255,.85); line-height: 1.6; font-size: var(--fs--1); }
.album-open { display: inline-flex; align-items: center; gap: .75rem; color: #fff; font-size: var(--fs--1); }
.album-open :deep(svg) { transition: transform .2s; }
.album-card:hover .album-open :deep(svg) { transform: translateX(4px); }
@media (max-width: 991px) { .gallery-albums { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 575px) { .gallery-albums { grid-template-columns: 1fr; gap: 1.25rem; } .album-image { aspect-ratio: 1; } }
@media (prefers-reduced-motion: reduce) { .album-image img, .album-open :deep(svg) { transition: none; } }
</style>
